/* ===================================================================
   More menu (mobile) -- ported from the prototype ("Rhody Reviews.dc.html",
   isMore section @2752). On phones the bottom tab bar shows the four primary
   destinations plus "More"; this screen is where every secondary destination
   lives, grouped AUTOMATE / GROW, with Tutorials / Settings / Log out at the
   bottom. (The prototype's Appearance toggle at the top is RETIRED -- Steve,
   2026-08-06: the app is outdoor high-contrast, always.)

   Structure, grouping, order, icons and copy otherwise match the prototype
   1:1. Rendered only on mobile widths (the /more route is reachable from the
   bottom-tab "More" item, which is hidden on desktop where the full sidebar
   already exposes these).
   =================================================================== */
import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LogoutButton } from '../components/LogoutButton';
import '../styles/moremenu.css';

const I = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>{d}</svg>
);
const CHEV = <path d="M9 6l6 6-6 6" />;

type Row = { to: string; label: string; icon: ReactNode };

const AUTOMATE: Row[] = [
  { to: '/templates', label: 'Templates', icon: I(<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>) },
  { to: '/automations', label: 'Automations', icon: I(<path d="M13 2L4 14h6l-1 8 9-12h-6z" />) },
  { to: '/replies', label: 'Your replies', icon: I(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />) },
  { to: '/feedback-gate', label: 'Private feedback', icon: I(<path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />) },
];

const GROW: Row[] = [
  { to: '/integrations', label: 'Integrations', icon: I(<><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" /></>) },
  { to: '/listings', label: 'NAP', icon: I(<><path d="M12 21s-7-6-7-11a7 7 0 0 1 14 0c0 5-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>) },
  { to: '/widget', label: 'Site Widget', icon: I(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18" /></>) },
  /* Collection row PAUSED 2026-08-07 (Steve) - QR Collection, may return.
     Withdrawn from the mobile menu too (it shipped without a QR generator).
     Same treatment as the desktop rail in AppShell.tsx and Social Posting
     below: hidden, not deleted; /collection redirects to /dashboard (App.tsx).
     Restore this row when the QR Collection screen returns. */
  // { to: '/collection', label: 'Collection', icon: I(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>) },
  /* Social Posting HIDDEN on mobile too (Brad, 2026-08-06). It was hidden from
     the desktop rail on 2026-07-06 because publishing to Meta is not live
     (gated on Meta App Review), but this mobile row was missed -- so phone
     owners could still reach a feature that cannot publish while desktop
     owners could not see it at all. Same treatment as the desktop entry in
     AppShell.tsx: hidden, not deleted, /social route still mounted. Re-add
     both entries together when publishing works. */
  // { to: '/social', label: 'Social Posting', icon: I(<><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="M8.2 10.8l7.6-4.4M8.2 13.2l7.6 4.4" /></>) },
  { to: '/google-posts', label: 'Google Posts', icon: I(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 13h8M8 16h5" /></>) },
  { to: '/competitors', label: 'Competitors', icon: I(<path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />) },
  { to: '/reports', label: 'Reports', icon: I(<path d="M5 21V8M12 21V3M19 21v-9" />) },
];

const BOTTOM: Row[] = [
  /* Tutorials HIDDEN (Steve, 2026-08-06) -- the guides are stale after today's
     UI pass and would point owners at controls that no longer exist. Mirrors
     the desktop rail in AppShell.tsx, where the full reasoning lives. Hidden,
     not deleted; the /tutorials route stays mounted for existing bookmarks.
     Restore this row when the guides are refreshed. */
  // { to: '/tutorials', label: 'Tutorials', icon: I(<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />) },
  { to: '/settings', label: 'Settings', icon: I(<><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" /></>) },
];

function MoreRow({ r }: { r: Row }) {
  return (
    <NavLink to={r.to} className="moremenu__row">
      <span className="moremenu__ico">{r.icon}</span>
      <span className="moremenu__label">{r.label}</span>
      <svg className="moremenu__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>{CHEV}</svg>
    </NavLink>
  );
}

export function MoreMenu() {
  return (
    <div className="moremenu">
      <div className="moremenu__h1">More</div>

      {/* The Appearance toggle that used to head this menu is RETIRED (Steve,
          2026-08-06). The app is outdoor high-contrast, always. */}
      {/* Same section hues as the desktop rail, so a phone and a laptop teach
          the owner the same colour language. Mapping locked in tokens.css. */}
      <div className="moremenu__group hue-blue">
        <div className="moremenu__sec">AUTOMATE</div>
        {AUTOMATE.map((r) => <MoreRow key={r.to} r={r} />)}
      </div>

      <div className="moremenu__group hue-green">
        <div className="moremenu__sec">GROW</div>
        {GROW.map((r) => <MoreRow key={r.to} r={r} />)}
      </div>

      {/* Utility group: neutral on purpose (chrome, not work). */}
      <div className="moremenu__group hue-none">
        {BOTTOM.map((r) => <MoreRow key={r.to} r={r} />)}
        <LogoutButton variant="more" />
      </div>
    </div>
  );
}
